/**
 * NestedFolderBrowser Component
 *
 * Full-featured file browser with nested navigation, multi-file selection,
 * and real-time selection summary.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useBrowseSharePoint, usePrefetchFolder, useSelectAllFiles } from '@/hooks/useSharePoint';
import { useFileSelection, type SelectedExcelFile } from '@/hooks/useFileSelection';
import { type SharePointFile, type SharePointFolder } from '@/services/api';
import { toast } from 'sonner';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  FolderOpen,
  FileSpreadsheet,
  X,
  Trash2,
  ChevronRight,
  AlertCircle,
  Search,
  Check,
  CheckCheck,
  ArrowDownWideNarrow,
  ArrowDownAZ,
  Loader2,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

type FileSortOrder = 'recent' | 'name';

const SORT_STORAGE_KEY = 'lotus-file-sort-order';

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface NestedFolderBrowserProps {
  /**
   * Initial path to start browsing from
   */
  initialPath?: string;

  /**
   * Callback when selection changes
   */
  onSelectionChange: (files: SelectedExcelFile[]) => void;

  /**
   * Maximum number of files that can be selected (only applies in multi-select mode)
   */
  maxSelections?: number;

  /**
   * Whether to allow multiple file selections
   */
  multiSelect?: boolean;

  /**
   * Whether to show the selection summary panel
   */
  showSummary?: boolean;

  /**
   * Optional title for the browser
   */
  title?: string;

  /**
   * Optional description
   */
  description?: string;

  /**
   * Optional drive ID
   */
  driveId?: string;

  /**
   * Whether to show a "Select All" button for Excel files
   */
  allowSelectAll?: boolean;

  /**
   * Whether to show an "Include subfolders" toggle (requires allowSelectAll)
   */
  allowRecursiveSelect?: boolean;
}

export function NestedFolderBrowser({
  initialPath = '/',
  onSelectionChange,
  maxSelections,
  multiSelect = true,
  showSummary = true,
  title = 'Browse Files',
  description = 'Navigate folders and select Excel files',
  driveId,
  allowSelectAll = false,
  allowRecursiveSelect = false,
}: NestedFolderBrowserProps) {
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [sortOrder, setSortOrder] = useState<FileSortOrder>(() => {
    try {
      const stored = localStorage.getItem(SORT_STORAGE_KEY);
      return stored === 'name' ? 'name' : 'recent';
    } catch {
      return 'recent';
    }
  });

  const handleSortChange = useCallback((order: FileSortOrder) => {
    setSortOrder(order);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, order);
    } catch { /* ignore storage errors */ }
  }, []);

  // Include subfolders toggle state
  const [includeSubfolders, setIncludeSubfolders] = useState(false);

  // File selection state
  const {
    selectedFiles,
    addFiles,
    toggleFile,
    removeFile,
    removeFiles,
    clearAll,
    getFileList,
    hasFile,
    count,
  } = useFileSelection(multiSelect);

  // Fetch folder contents with Excel filtering (infinite scroll)
  const {
    data, isLoading, error, refetch,
    hasNextPage, fetchNextPage, isFetchingNextPage,
  } = useBrowseSharePoint(currentPath, driveId, true);

  // Async "Select All" — fetches ALL files from backend when folder has more than loaded
  const selectAllScan = useSelectAllFiles(currentPath, driveId, includeSubfolders);

  // Infinite scroll: load next page when sentinel enters the ScrollArea viewport
  const scrollSentinelRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = scrollSentinelRef.current;
    if (!sentinel) return;

    // ScrollArea renders a Radix viewport — use it as the IntersectionObserver root
    const scrollViewport = scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]'
    ) as HTMLElement | null;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollViewport, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  // Auto-fetch next pages when no Excel files found yet
  // (e.g., first pages contain only folders or non-Excel files)
  const excelFileCount = data?.files?.length ?? 0;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading && excelFileCount === 0) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, excelFileCount, fetchNextPage]);

  // When async scan completes, batch-add all returned files
  useEffect(() => {
    if (!selectAllScan.data?.files?.length) return;

    const scannedFiles: SelectedExcelFile[] = selectAllScan.data.files.map(f => ({
      id: f.id,
      name: f.name,
      path: f.path,
      size: f.size,
      driveId: f.drive_id || data?.drive_id || driveId,
      siteId: f.site_id || data?.site_id,
      folderId: f.parent_id,
      webUrl: f.web_url,
      modifiedAt: f.modified_at,
    }));

    addFiles(scannedFiles);
  }, [selectAllScan.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show error toast if scan fails
  useEffect(() => {
    if (selectAllScan.error) {
      toast.error(`Failed to load all files: ${selectAllScan.error.message}`);
    }
  }, [selectAllScan.error]);

  // Notify parent of selection changes
  React.useEffect(() => {
    onSelectionChange(getFileList());
  }, [selectedFiles, onSelectionChange, getFileList]);

  // Navigate to a folder
  const navigateToFolder = (path: string) => {
    setSearchQuery('');
    setCurrentPath(path);
  };

  // Categorize items (must be before handlers that reference them)
  const allFolders = data?.folders || [];
  const allExcelFiles = useMemo(() => data?.files || [], [data?.files]);
  const breadcrumbs = data?.breadcrumbs || [{ name: 'Root', path: '/' }];
  const stats = data?.stats;

  // Handle file selection toggle
  const handleFileToggle = (file: {
    id: string;
    name: string;
    path: string;
    size: number;
    drive_id?: string;
    site_id?: string;
    parent_id?: string;
    web_url?: string;
    modified_at?: string;
  }) => {
    // Check max selections in multi-select mode
    if (multiSelect && maxSelections && !hasFile(file.id) && count >= maxSelections) {
      toast.error(`Maximum ${maxSelections} file${maxSelections > 1 ? 's' : ''} allowed`);
      return; // Don't allow selection beyond max
    }

    const selectedFile: SelectedExcelFile = {
      id: file.id,
      name: file.name,
      path: file.path,
      size: file.size,
      driveId: file.drive_id || data?.drive_id || driveId,
      siteId: file.site_id || data?.site_id,
      folderId: file.parent_id,
      webUrl: file.web_url,
      modifiedAt: file.modified_at,
    };

    toggleFile(selectedFile);
  };

  // Handle Select All / Deselect All
  const handleSelectAll = useCallback(() => {
    const filesToCheck = allExcelFiles;

    // Check if all loaded files are already selected (deselect mode)
    const allSelected = filesToCheck.length > 0 && filesToCheck.every(f => hasFile(f.id));

    if (allSelected) {
      // Deselect all current folder files (both loaded and scan-fetched)
      removeFiles(filesToCheck.map(f => f.id));
      return;
    }

    // If there are more files than currently loaded, trigger async scan
    if (data?.has_more) {
      selectAllScan.refetch();
      return;
    }

    // All files already loaded — select them instantly
    const newFiles = filesToCheck.filter(f => !hasFile(f.id));
    if (maxSelections && count + newFiles.length > maxSelections) {
      toast.warning(`Cannot select all — would exceed the ${maxSelections} file limit. ${maxSelections - count} slot(s) remaining.`);
      return;
    }

    const selectedExcelFiles: SelectedExcelFile[] = filesToCheck.map(f => ({
      id: f.id,
      name: f.name,
      path: f.path,
      size: f.size,
      driveId: f.drive_id || data?.drive_id || driveId,
      siteId: f.site_id || data?.site_id,
      folderId: f.parent_id,
      webUrl: f.web_url,
      modifiedAt: f.modified_at,
    }));

    addFiles(selectedExcelFiles);
  }, [allExcelFiles, hasFile, count, maxSelections, addFiles, removeFiles, data, driveId, selectAllScan]);

  // Format file size
  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round(bytes / Math.pow(k, i) * 10) / 10} ${sizes[i]}`;
  };

  // Filter items based on search query
  const folders = searchQuery
    ? allFolders.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : allFolders;

  const filteredExcelFiles = searchQuery
    ? allExcelFiles.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : allExcelFiles;

  const excelFiles = [...filteredExcelFiles].sort((a, b) => {
    if (sortOrder === 'name') {
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    }
    // 'recent': most recently modified first (backend default, but re-sort for toggle)
    const aDate = a.modified_at || '';
    const bDate = b.modified_at || '';
    return bDate.localeCompare(aDate);
  });

  // Combined list for keyboard navigation
  const allItems = [...folders, ...excelFiles];

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (allItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => Math.min(prev + 1, allItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < allItems.length) {
          const item = allItems[focusedIndex];
          if (focusedIndex < folders.length) {
            // It's a folder
            navigateToFolder(item.path);
          } else {
            // It's a file - cast to file type
            const file = item as SharePointFile;
            handleFileToggle(file);
          }
        }
        break;
      case ' ':
        e.preventDefault();
        if (focusedIndex >= folders.length && focusedIndex < allItems.length) {
          // Only toggle files, not folders - cast to file type
          const file = allItems[focusedIndex] as SharePointFile;
          handleFileToggle(file);
        }
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, focusedIndex, folders.length]);

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset focused index when path or search changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [currentPath, searchQuery]);

  return (
    <div className="space-y-4">
      {/* Main Navigation Area */}
      <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={crumb.path}>
                      {index > 0 && <BreadcrumbSeparator><ChevronRight className="h-4 w-4" /></BreadcrumbSeparator>}
                      <BreadcrumbItem>
                        {index === breadcrumbs.length - 1 ? (
                          <BreadcrumbPage>{crumb.name}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink
                            onClick={() => navigateToFolder(crumb.path)}
                            className="cursor-pointer hover:text-primary"
                          >
                            {crumb.name}
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            {/* Stats */}
            {stats && (
              <div className="flex gap-3 text-sm text-muted-foreground">
                <span>{stats.folder_count} folders</span>
                <span>•</span>
                <span>
                  {allExcelFiles.length} Excel files
                  {data?.total_items ? ` (${data.total_items} total items)` : ''}
                  {data?.has_more ? ' — loading more...' : ''}
                </span>
                {stats.total_size > 0 && (
                  <>
                    <span>•</span>
                    <span>{formatSize(stats.total_size)}</span>
                  </>
                )}
              </div>
            )}

            {/* Search/Filter */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by filename..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Separator />

            {/* Folder and File List */}
            <ScrollArea className="h-[400px]" ref={scrollAreaRef}>
              {isLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                  <p className="text-sm text-muted-foreground">
                    Failed to load folder contents
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {error instanceof Error ? error.message : 'Unknown error'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetch()}
                    className="mt-4"
                  >
                    Try Again
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Folders Section */}
                  {folders.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-1">
                        Folders
                      </p>
                      {folders.map((folder, index) => (
                        <FolderItemComponent
                          key={folder.id}
                          folder={folder}
                          onClick={() => navigateToFolder(folder.path)}
                          driveId={driveId}
                          isFocused={focusedIndex === index}
                          disablePrefetch={!!error}
                        />
                      ))}
                    </>
                  )}

                  {/* Excel Files Section */}
                  {excelFiles.length > 0 && (() => {
                    const allCurrentSelected = excelFiles.every(f => hasFile(f.id));
                    return (
                    <>
                      <div className="flex items-center justify-between px-2 py-1 mt-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Excel Files
                        </p>
                        <div className="flex items-center gap-3">
                          {/* Select All / Deselect All button */}
                          {allowSelectAll && multiSelect && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleSelectAll}
                              disabled={selectAllScan.isLoading}
                              className="h-7 text-xs gap-1.5"
                            >
                              {selectAllScan.isLoading ? (
                                <>
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  {selectAllScan.progress?.filesFound
                                    ? `Loading... (${selectAllScan.progress.filesFound} found)`
                                    : 'Loading all files...'}
                                </>
                              ) : allCurrentSelected ? (
                                <>
                                  <X className="h-3 w-3" />
                                  Deselect All
                                </>
                              ) : (
                                <>
                                  <CheckCheck className="h-3 w-3" />
                                  Select All ({data?.total_items ?? excelFiles.length})
                                </>
                              )}
                            </Button>
                          )}
                          {/* Include subfolders toggle */}
                          {allowSelectAll && allowRecursiveSelect && multiSelect && (
                            <div className="flex items-center gap-1.5">
                              <Checkbox
                                id="include-subfolders"
                                checked={includeSubfolders}
                                onCheckedChange={(checked) => setIncludeSubfolders(checked === true)}
                                className="h-3.5 w-3.5"
                              />
                              <Label htmlFor="include-subfolders" className="text-xs text-muted-foreground cursor-pointer">
                                Include subfolders
                              </Label>
                            </div>
                          )}
                          <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
                            <button
                              onClick={() => handleSortChange('recent')}
                              className={cn(
                                'flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors',
                                sortOrder === 'recent'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:text-foreground'
                              )}
                              title="Sort by most recent"
                            >
                              <ArrowDownWideNarrow className="h-3 w-3" />
                              Recent
                            </button>
                            <button
                              onClick={() => handleSortChange('name')}
                              className={cn(
                                'flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors',
                                sortOrder === 'name'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:text-foreground'
                              )}
                              title="Sort by name A-Z"
                            >
                              <ArrowDownAZ className="h-3 w-3" />
                              Name
                            </button>
                          </div>
                        </div>
                      </div>
                      {excelFiles.map((file, index) => (
                        <FileItemComponent
                          key={file.id}
                          file={file}
                          isSelected={hasFile(file.id)}
                          onToggle={() => handleFileToggle(file)}
                          formatSize={formatSize}
                          isFocused={focusedIndex === folders.length + index}
                        />
                      ))}
                    </>
                    );
                  })()}

                  {/* Infinite scroll sentinel + loading indicator */}
                  <div ref={scrollSentinelRef} className="h-1" />
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center gap-2 py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Loading more files...</span>
                    </div>
                  )}

                  {/* Empty State */}
                  {folders.length === 0 && excelFiles.length === 0 && (
                    <div className="flex flex-col items-center justify-center p-12 text-center">
                      <FolderOpen className="h-16 w-16 text-muted-foreground/30 mb-4" />
                      <p className="text-sm text-muted-foreground">
                        No folders or Excel files in this location
                      </p>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

      {/* Selection Summary */}
      {showSummary && (
        <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Selected Files</CardTitle>
                <div role="status" aria-live="polite" aria-atomic="true">
                  <Badge variant="secondary">{count}</Badge>
                  <span className="sr-only">{count} file{count !== 1 ? 's' : ''} selected</span>
                </div>
              </div>
              {maxSelections && multiSelect && (
                <>
                  <CardDescription>
                    {count} of {maxSelections} files selected
                  </CardDescription>
                  <Progress
                    value={(count / maxSelections) * 100}
                    className="mt-2 h-2"
                  />
                </>
              )}
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {count === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center">
                    <FileSpreadsheet className="h-12 w-12 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No files selected yet
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {multiSelect
                        ? 'Select Excel files from the list'
                        : 'Select an Excel file'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {getFileList().map((file) => (
                      <div
                        key={file.id}
                        className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border group"
                      >
                        <FileSpreadsheet className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{file.path}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatSize(file.size)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.id)}
                          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {count > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Total: {formatSize(getFileList().reduce((sum, f) => sum + f.size, 0))}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAll}
                      className="gap-2"
                    >
                      <Trash2 className="h-3 w-3" />
                      Clear All
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
      )}
    </div>
  );
}

// Folder Item Component with Prefetching
function FolderItemComponent({
  folder,
  onClick,
  driveId,
  isFocused,
  disablePrefetch,
}: {
  folder: SharePointFolder;
  onClick: () => void;
  driveId?: string;
  isFocused?: boolean;
  disablePrefetch?: boolean;
}) {
  const prefetch = usePrefetchFolder(folder.path, driveId, true);

  return (
    <button
      onClick={onClick}
      onMouseEnter={disablePrefetch ? undefined : prefetch}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg border transition-all',
        'border-border hover:border-primary/50 hover:bg-muted/50',
        'text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        isFocused && 'ring-2 ring-primary ring-offset-2'
      )}
      aria-label={`Navigate to ${folder.name}, ${folder.child_count || 0} items`}
    >
      <FolderOpen className="h-5 w-5 text-primary flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{folder.name}</p>
        <p className="text-xs text-muted-foreground">
          {folder.child_count || 0} items
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    </button>
  );
}

// File Item Component with Selection
function FileItemComponent({
  file,
  isSelected,
  onToggle,
  formatSize,
  isFocused,
}: {
  file: SharePointFile;
  isSelected: boolean;
  onToggle: () => void;
  formatSize: (bytes: number) => string;
  isFocused?: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg border transition-all',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-muted/50',
        'text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        isFocused && 'ring-2 ring-primary ring-offset-2'
      )}
      role="checkbox"
      aria-checked={isSelected}
      aria-label={`${isSelected ? 'Deselect' : 'Select'} ${file.name}`}
    >
      <div
        className={cn(
          "h-4 w-4 shrink-0 rounded-sm border ring-offset-background flex items-center justify-center",
          isSelected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-primary"
        )}
        aria-hidden="true"
      >
        {isSelected && <Check className="h-4 w-4" />}
      </div>
      <FileSpreadsheet className="h-5 w-5 text-success flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{file.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatSize(file.size)}
          {file.modified_at && <> · Modified {formatRelativeTime(file.modified_at)}</>}
        </p>
      </div>
    </button>
  );
}
